import { Injectable } from '@angular/core';
import { AlarisApiService } from '@campaign-portal/components-library';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import { ExportService } from '@helpers/api/export.service';
import { HlrConfigService } from '@helpers/providers/hlr-config.service';
import {
	DeleteRequest,
	MnpCreateUpdateResponse,
	MnpCrudService,
	MnpDeleteResponse,
	MnpReadRequest,
	MnpReadResponse,
} from '@helpers/types/api';
import { AiRule } from '@helpers/types/models';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { AiRulesFieldsService } from './ai-rules.fields.service';

export interface AiSources {
	id: number;
	name: string;
}

@Injectable({
	providedIn: 'root',
})
export class AiRulesService implements MnpCrudService<AiRule<number>> {
	activePage = 0;
	pageSize = 50;

	readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private readonly api: AlarisApiService,
		private readonly exportService: ExportService,
		private readonly config: HlrConfigService,
		private readonly fields: AiRulesFieldsService
	) {}

	read(params: MnpReadRequest): Observable<Required<RPCResult<AiRule<number>[]>>> {
		return this.api.loader<MnpReadResponse<AiRule<number>>>('AIRules.Read', params, this.loading$);
	}

	update(params: AiRule<number>): Observable<MnpCreateUpdateResponse> {
		const method = params.id === null ? 'AIRules.Create' : 'AIRules.Update';
		return this.api.loader<MnpCreateUpdateResponse>(method, params, this.loading$);
	}

	delete(params: DeleteRequest): Observable<MnpDeleteResponse> {
		return this.api.loader<MnpDeleteResponse>('AIRules.Delete', params, this.loading$);
	}

	getRelatedSources(): Observable<AiSources[]> {
		return this.api
			.loader<{ data: AiSources[] }>('AIRules.GetRelatedSources', {}, this.loading$)
			.pipe(map((res) => res.data));
	}

	export(total: number, params: MnpReadRequest): Observable<void> {
		const exportParams = {
			...params,
			offset: 0,
			limit: total,
		};
		return this.exportService.export(this.config.api, 'AIRules.Read', exportParams, this.fields.headers, 'ai_rules');
	}
}
